import type { ChatParticipantAddedEvent } from './chatParticipantAddedNote';

function parseFirestoreTimestamp(value: object): number | null {
  if ('toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().getTime();
  }

  const record = value as {
    seconds?: number;
    _seconds?: number;
    nanoseconds?: number;
    _nanoseconds?: number;
  };
  const seconds = record.seconds ?? record._seconds;
  if (typeof seconds !== 'number') {
    return null;
  }
  const nanos = record.nanoseconds ?? record._nanoseconds ?? 0;
  return seconds * 1000 + Math.floor(nanos / 1_000_000);
}

export function getChatTimestampMs(value: unknown): number {
  if (!value) {
    return 0;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  if (typeof value === 'object' && value !== null) {
    return parseFirestoreTimestamp(value) ?? 0;
  }

  return 0;
}

export function resolveParticipantAddedEventSortMs(
  event: Pick<ChatParticipantAddedEvent, 'inviteeAdminExternalId' | 'timestamp'>,
  messages: Array<{ senderId?: string; senderRole?: string; timestamp?: unknown }>,
): number {
  let sortMs = getChatTimestampMs(event.timestamp);

  let firstInviteeMessageMs = Infinity;
  for (const message of messages) {
    if (message.senderRole !== 'admin' || message.senderId !== event.inviteeAdminExternalId) {
      continue;
    }
    const messageMs = getChatTimestampMs(message.timestamp);
    if (messageMs > 0) {
      firstInviteeMessageMs = Math.min(firstInviteeMessageMs, messageMs);
    }
  }

  if (firstInviteeMessageMs !== Infinity && (sortMs === 0 || sortMs >= firstInviteeMessageMs)) {
    sortMs = firstInviteeMessageMs - 1;
  }

  return sortMs;
}

export type ChatTimelineSortItem = {
  kind: 'message' | 'event';
  timestamp: unknown;
  sortMs?: number;
};

export function compareChatTimelineItems(
  left: ChatTimelineSortItem,
  right: ChatTimelineSortItem,
): number {
  const leftMs = left.sortMs ?? getChatTimestampMs(left.timestamp);
  const rightMs = right.sortMs ?? getChatTimestampMs(right.timestamp);
  if (leftMs !== rightMs) {
    return leftMs - rightMs;
  }
  if (left.kind !== right.kind) {
    return left.kind === 'event' ? -1 : 1;
  }
  return 0;
}
